import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useGorgiasIntegrationQuery } from "modules/settings/hooks/gorgias/useGorgiasIntegrationQuery"
import { useUpsertGorgiasIntegrationMutation } from "modules/settings/hooks/gorgias/useUpsertGorgiasIntegrationMutation"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { UpsertGorgiasIntegrationSchema } from "schema"
import { toast } from "sonner"
import z from "zod"

export const GorgiasIntegrationModal = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: employeeData } = useAuthEmployeeQuery() ?? {}
  const { mutateAsync, isPending } = useUpsertGorgiasIntegrationMutation()

  const { data, isPending: isIntegrationPending } = useGorgiasIntegrationQuery({
    onSuccess: (data) => {
      form.setValue("baseApiUrl", data.data?.baseApiUrl ?? "")
      form.setValue("username", data.data?.username ?? employeeData?.email ?? "")
    },
  })

  const form = useForm<z.infer<typeof UpsertGorgiasIntegrationSchema>>({
    resolver: zodResolver(UpsertGorgiasIntegrationSchema),
    defaultValues: data ?? {
      baseApiUrl: "",
      token: "",
      username: "",
    },
    mode: "all",
  })

  function onOpenChange(isOpen: boolean = false) {
    if (isPending) return

    if (!isOpen) {
      navigate({ to: "/settings/integrations/gorgias" })
    }
  }

  async function onSubmit(data: z.infer<typeof UpsertGorgiasIntegrationSchema>) {
    await mutateAsync(data)
    toast.success(t("settings.integrations.gorgias.integrationCard.success"))
    onOpenChange(false)
  }

  if (isIntegrationPending) return null

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader className="flex flex-col gap-2 items-start">
          <DialogTitle>{t("settings.integrations.gorgias.integrationCard.title")}</DialogTitle>
          <DialogDescription>
            {t("settings.integrations.gorgias.integrationCard.description")}{" "}
            <Popover>
              <PopoverTrigger slick>
                <span>{t("settings.integrations.gorgias.integrationCard.help.tooltip")}</span>
              </PopoverTrigger>
              <PopoverContent className="max-w-96 flex flex-col gap-2">
                <h3 className="font-medium">
                  {t("settings.integrations.gorgias.integrationCard.help.tooltip")}
                </h3>
                <ul className="list-decimal list-inside text-left text-sm space-y-1">
                  <li>{t("settings.integrations.gorgias.integrationCard.help.step1")}</li>
                  <li>{t("settings.integrations.gorgias.integrationCard.help.step2")}</li>
                  <li>{t("settings.integrations.gorgias.integrationCard.help.step3")}</li>
                </ul>
              </PopoverContent>
            </Popover>
          </DialogDescription>
        </DialogHeader>
        <div>
          <Form
            {...form}
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
            errorTranslations={getErrorTranslations()}
          >
            <FormField
              control={form.control}
              name="baseApiUrl"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>
                    {t("settings.integrations.gorgias.integrationCard.form.baseApiUrl")}
                  </FormLabel>
                  <FormControl>
                    <div className="flex items-center gap-0">
                      <Input
                        {...field}
                        required
                        placeholder={`https://${employeeData?.company?.name?.toLowerCase().replace(" ", "")}.gorgias.com/api/`}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>
                    {t("settings.integrations.gorgias.integrationCard.form.username")}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} required placeholder={employeeData?.email} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="token"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>
                    {t("settings.integrations.gorgias.integrationCard.form.token")}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} required placeholder="1234567890" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button disabled={form.formState.isLoading || !form.formState.isValid || isPending}>
                {t("settings.integrations.gorgias.integrationCard.form.submit")}
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
                {t("shared.cancel")}
              </Button>
            </DialogFooter>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}
